import * as chrono from 'chrono-node'
import { startOfDay } from 'date-fns'
import { DUE_PRESETS } from './duePresets'
import type { DuePreset } from './duePresets'
import type {
  FilterFieldKey,
  FilterOperator,
  FilterValue,
  TaskFilter,
} from '@/lib/types/filter'

/**
 * Typed filters (design 9b): `status:progress,planned @me due:<fri`.
 * Each token becomes a normal filter chip, so everything typed here can be
 * edited afterwards with the regular pickers.
 */

export interface FilterQueryContext {
  statuses: ReadonlyArray<{ id: string; name: string; isOpen: boolean }>
  members: ReadonlyArray<{ userId: string; name: string }>
  labels: ReadonlyArray<{ id: string; name: string }>
  cycles: ReadonlyArray<{ id: string; name: string }>
  projects: ReadonlyArray<{ id: string; name: string }>
  currentUserId: string | null
  currentCycleId: string | null
  now?: Date
}

export type FilterTokenError =
  | 'unknownField'
  | 'noMatch'
  | 'badDate'
  | 'unsupported'

export type ParsedFilterToken =
  | {
      raw: string
      ok: true
      filter: TaskFilter
      /** Other readings of the same token ("due on Fri" for `due:<fri`). */
      alternatives: Array<TaskFilter>
    }
  | { raw: string; ok: false; error: FilterTokenError }

export interface ParsedFilterQuery {
  tokens: Array<ParsedFilterToken>
  /** One filter per property, in the order first typed. */
  filters: Array<TaskFilter>
}

const FIELD_ALIASES: Record<string, FilterFieldKey> = {
  status: 'status',
  s: 'status',
  priority: 'priority',
  pri: 'priority',
  p: 'priority',
  assignee: 'assignee',
  assigned: 'assignee',
  a: 'assignee',
  by: 'createdBy',
  creator: 'createdBy',
  created: 'createdBy',
  createdby: 'createdBy',
  author: 'createdBy',
  label: 'labels',
  labels: 'labels',
  tag: 'labels',
  l: 'labels',
  cycle: 'cycle',
  sprint: 'cycle',
  c: 'cycle',
  project: 'project',
  proj: 'project',
  due: 'due',
  d: 'due',
  title: 'name',
  name: 'name',
  t: 'name',
}

const PRIORITY_NAMES: Record<string, number> = {
  none: 0,
  no: 0,
  low: 1,
  medium: 2,
  med: 2,
  high: 3,
  urgent: 4,
}

const DUE_PRESET_ALIASES: Record<string, DuePreset> = {
  overdue: 'overdue',
  late: 'overdue',
  today: 'today',
  tomorrow: 'tomorrow',
  tmr: 'tomorrow',
  week: 'this_week',
  thisweek: 'this_week',
  next7: 'next_7_days',
  none: 'no_date',
  nodate: 'no_date',
  ...Object.fromEntries(DUE_PRESETS.map((p) => [p, p])),
}

// key:value (value may be quoted), @mention (may be quoted), quoted words, or words.
const TOKEN_RE = /-?[A-Za-z]+:(?:"[^"]*"?|\S*)|-?@(?:"[^"]*"?|\S*)|"[^"]*"?|\S+/g
const KEYED_RE = /^-?([A-Za-z]+):/

function unquote(s: string): string {
  return s.replace(/^"/, '').replace(/"$/, '')
}

function norm(s: string): string {
  return s.trim().toLowerCase()
}

/**
 * True when the text uses filter syntax at all (a known `key:` or an
 * `@mention`), so plain searches in the Filter menu stay plain searches.
 */
export function looksLikeFilterQuery(text: string): boolean {
  for (const raw of text.match(TOKEN_RE) ?? []) {
    const body = raw.replace(/^-/, '')
    if (body.startsWith('@') && body.length > 1) return true
    const key = KEYED_RE.exec(raw)?.[1]
    if (key && norm(key) in FIELD_ALIASES) return true
  }
  return false
}

/** Exact name, then starts-with, then contains. */
function matchByName<T>(
  items: ReadonlyArray<T>,
  name: (item: T) => string,
  query: string,
): T | undefined {
  const q = norm(query)
  if (!q) return undefined
  return (
    items.find((i) => norm(name(i)) === q) ??
    items.find((i) => norm(name(i)).startsWith(q)) ??
    items.find((i) => norm(name(i)).split(/\s+/).some((w) => w.startsWith(q))) ??
    items.find((i) => norm(name(i)).includes(q))
  )
}

function parseDay(text: string, now: Date): number | null {
  const date = chrono.parseDate(text, now, { forwardDate: true })
  return date ? startOfDay(date).getTime() : null
}

type ValueResult = { values: Array<FilterValue> } | { error: FilterTokenError }

function resolveMember(q: string, ctx: FilterQueryContext, allowNone: boolean): ValueResult {
  const n = norm(q)
  if (n === 'me') {
    return ctx.currentUserId ? { values: [ctx.currentUserId] } : { error: 'noMatch' }
  }
  if (allowNone && (n === 'none' || n === 'unassigned' || n === 'nobody')) {
    return { values: [''] }
  }
  const m = matchByName(ctx.members, (x) => x.name, q)
  return m ? { values: [m.userId] } : { error: 'noMatch' }
}

/** Resolve a comma-separated list through `one`; any miss fails the token. */
function resolveList(
  raw: string,
  one: (part: string) => ValueResult,
): ValueResult {
  const values: Array<FilterValue> = []
  for (const part of raw.split(',').filter((p) => p.trim())) {
    const r = one(part)
    if ('error' in r) return r
    for (const v of r.values) if (!values.includes(v)) values.push(v)
  }
  return values.length ? { values } : { error: 'noMatch' }
}

function resolveSelect(
  field: FilterFieldKey,
  raw: string,
  ctx: FilterQueryContext,
): ValueResult {
  switch (field) {
    case 'status':
      return resolveList(raw, (part) => {
        const s = matchByName(ctx.statuses, (x) => x.name, part)
        if (s) return { values: [s.id] }
        const n = norm(part)
        if (n === 'open' || n === 'closed') {
          const ids = ctx.statuses.filter((x) => x.isOpen === (n === 'open')).map((x) => x.id)
          return ids.length ? { values: ids } : { error: 'noMatch' }
        }
        return { error: 'noMatch' }
      })
    case 'assignee':
      return resolveList(raw, (part) => resolveMember(part, ctx, true))
    case 'createdBy':
      return resolveList(raw, (part) => resolveMember(part, ctx, false))
    case 'labels':
      return resolveList(raw, (part) => {
        const l = matchByName(ctx.labels, (x) => x.name, part)
        return l ? { values: [l.id] } : { error: 'noMatch' }
      })
    case 'project':
      return resolveList(raw, (part) => {
        const p = matchByName(ctx.projects, (x) => x.name, part)
        return p ? { values: [p.id] } : { error: 'noMatch' }
      })
    case 'cycle':
      return resolveList(raw, (part) => {
        if (norm(part) === 'current') {
          return ctx.currentCycleId ? { values: [ctx.currentCycleId] } : { error: 'noMatch' }
        }
        const c = matchByName(ctx.cycles, (x) => x.name, part)
        return c ? { values: [c.id] } : { error: 'noMatch' }
      })
    case 'priority':
      return resolveList(raw, (part) => {
        const n = norm(part)
        if (/^[0-4]$/.test(n)) return { values: [Number(n)] }
        const p = PRIORITY_NAMES[n]
        return p === undefined ? { error: 'noMatch' } : { values: [p] }
      })
    default:
      return { error: 'unsupported' }
  }
}

function parsePriority(raw: string, negated: boolean, ctx: FilterQueryContext): ParsedFilterToken | ValueResult {
  // p:>=high, p:≥high, p:high+ read as "at least"; p:>medium as "above".
  const m = /^(>=|≥|>)?(.*?)(\+)?$/.exec(raw)
  const prefix = m?.[1]
  const body = m?.[2] ?? raw
  const plus = !!m?.[3]
  if (!prefix && !plus) {
    return resolveSelect('priority', raw, ctx)
  }
  if (negated) return { error: 'unsupported' }
  const r = resolveSelect('priority', body, ctx)
  if ('error' in r) return r
  const min = Math.min(...r.values.map(Number)) + (prefix === '>' ? 1 : 0)
  if (min > 4) return { error: 'noMatch' }
  return {
    raw,
    ok: true,
    filter: { fieldKey: 'priority', operator: 'at_least', values: [min] },
    alternatives: [],
  }
}

function parseDue(raw: string, negated: boolean, now: Date): ParsedFilterToken | { error: FilterTokenError } {
  const m = /^([<>=])?(.*)$/.exec(raw)
  const sign = m?.[1]
  const body = unquote(m?.[2] ?? '')

  if (!sign) {
    const presets = body.split(',').map((p) => DUE_PRESET_ALIASES[norm(p)])
    if (presets.every((p): p is DuePreset => !!p)) {
      return {
        raw,
        ok: true,
        filter: { fieldKey: 'due', operator: negated ? 'excludes' : 'includes', values: presets },
        alternatives: [],
      }
    }
  }
  if (negated) return { error: 'unsupported' }

  const day = parseDay(body, now)
  if (day === null) return { error: 'badDate' }
  const operator: FilterOperator = sign === '<' ? 'before' : sign === '>' ? 'after' : 'on'
  return {
    raw,
    ok: true,
    filter: { fieldKey: 'due', operator, values: [day] },
    alternatives:
      operator === 'on' ? [] : [{ fieldKey: 'due', operator: 'on', values: [day] }],
  }
}

function parseToken(raw: string, ctx: FilterQueryContext, now: Date): ParsedFilterToken | null {
  const negated = raw.startsWith('-') && raw.length > 1
  const body = negated ? raw.slice(1) : raw
  const fail = (error: FilterTokenError): ParsedFilterToken => ({ raw, ok: false, error })
  const ok = (filter: TaskFilter): ParsedFilterToken => ({ raw, ok: true, filter, alternatives: [] })

  if (body.startsWith('@')) {
    const who = unquote(body.slice(1))
    if (!who) return null
    const r = resolveMember(who, ctx, true)
    if ('error' in r) return fail(r.error)
    return ok({ fieldKey: 'assignee', operator: negated ? 'excludes' : 'includes', values: r.values })
  }

  const keyed = KEYED_RE.exec(raw)
  if (!keyed) return null
  const field = FIELD_ALIASES[norm(keyed[1] ?? '')]
  if (!field) return fail('unknownField')
  const value = unquote(raw.slice(keyed[0].length))
  if (!value) return null

  if (field === 'name') {
    return ok({ fieldKey: 'name', operator: negated ? 'does_not_contain' : 'contains', values: [value] })
  }
  if (field === 'due') {
    const r = parseDue(raw.slice(keyed[0].length), negated, now)
    return 'ok' in r ? { ...r, raw } : fail(r.error)
  }
  if (field === 'priority') {
    const r = parsePriority(value, negated, ctx)
    if ('ok' in r) return { ...r, raw }
    if ('error' in r) return fail(r.error)
    return ok({ fieldKey: 'priority', operator: negated ? 'excludes' : 'includes', values: r.values })
  }

  const r = resolveSelect(field, value, ctx)
  if ('error' in r) return fail(r.error)
  return ok({ fieldKey: field, operator: negated ? 'excludes' : 'includes', values: r.values })
}

const OR_RE = /^(?:or|\|)$/i

/** Tokens of one OR group → its filters, one per property. */
function groupFilters(tokens: ReadonlyArray<ParsedFilterToken>): Array<TaskFilter> {
  // Same-operator lists combine, otherwise last wins.
  const byField = new Map<FilterFieldKey, TaskFilter>()
  for (const token of tokens) {
    if (!token.ok) continue
    const next = token.filter
    const prev = byField.get(next.fieldKey)
    const combinable =
      prev &&
      prev.operator === next.operator &&
      (next.operator === 'includes' || next.operator === 'excludes')
    byField.set(
      next.fieldKey,
      combinable
        ? { ...prev, values: [...new Set([...prev.values, ...next.values])] }
        : next,
    )
  }
  return [...byField.values()]
}

/**
 * Parse typed filter text. Words without a `key:` become "Title contains",
 * so `@me status:open login bug` finds my open tasks mentioning "login bug".
 * A standalone `or` (or `|`) starts an OR group (design 9b):
 * `@me cycle:current or due:overdue`.
 */
export function parseFilterQuery(text: string, ctx: FilterQueryContext): ParsedFilterQuery {
  const now = ctx.now ?? new Date()
  const groups: Array<Array<ParsedFilterToken>> = [[]]
  let words: Array<string> = []

  const closeGroup = () => {
    const title = words.join(' ').trim()
    if (title) {
      groups[groups.length - 1]?.push({
        raw: title,
        ok: true,
        filter: { fieldKey: 'name', operator: 'contains', values: [title] },
        alternatives: [],
      })
    }
    words = []
  }

  for (const raw of text.match(TOKEN_RE) ?? []) {
    if (OR_RE.test(raw)) {
      closeGroup()
      groups.push([])
      continue
    }
    const parsed = parseToken(raw, ctx, now)
    if (parsed) groups[groups.length - 1]?.push(parsed)
    else if (!KEYED_RE.test(raw) && !/^-?@/.test(raw)) words.push(unquote(raw))
  }
  closeGroup()

  // Dangling `or`s ("status:open or") don't make a group.
  const nonEmpty = groups.filter((g) => g.some((t) => t.ok))
  const grouped = nonEmpty.length > 1
  const withGroup = (f: TaskFilter, i: number): TaskFilter => (grouped ? { ...f, group: i } : f)

  const tokens: Array<ParsedFilterToken> = []
  const filters: Array<TaskFilter> = []
  for (const g of groups) {
    const i = nonEmpty.indexOf(g)
    for (const token of g) {
      tokens.push(
        token.ok && i >= 0
          ? {
              ...token,
              filter: withGroup(token.filter, i),
              alternatives: token.alternatives.map((alt) => withGroup(alt, i)),
            }
          : token,
      )
    }
    if (i >= 0) filters.push(...groupFilters(g).map((f) => withGroup(f, i)))
  }
  return { tokens, filters }
}
